/**
 * 浏览器兼容的文件操作工具
 * 替代 NW.js 的 window.require("fs") 和 window.require("path")
 */

/**
 * 读取用户在 <input type="file"> 中选择的文件内容
 * 替代: fs.readFileSync(fileInput.value).toString()
 *
 * @param {HTMLInputElement} fileInput - 文件选择 input 元素
 * @param {string} [encoding] - 文本编码，默认 'utf-8'
 * @returns {Promise<string>} 文件文本内容
 */
export function readFileAsText(fileInput, encoding) {
    return new Promise((resolve, reject) => {
        const file = fileInput.files?.[0]
        if (!file) {
            reject(new Error('未选择文件'))
            return
        }
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error)
        reader.readAsText(file, encoding || 'utf-8')
    })
}

/**
 * 读取文件为 ArrayBuffer（用于 BOM 检测和重新编码）
 *
 * @param {HTMLInputElement} fileInput - 文件选择 input 元素
 * @returns {Promise<ArrayBuffer>}
 */
export function readFileAsArrayBuffer(fileInput) {
    return new Promise((resolve, reject) => {
        const file = fileInput.files?.[0]
        if (!file) {
            reject(new Error('未选择文件'))
            return
        }
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error)
        reader.readAsArrayBuffer(file)
    })
}

/**
 * 从 ArrayBuffer 解码文本
 *
 * @param {ArrayBuffer} buffer
 * @param {string} encoding - 如 'utf-8', 'gbk'
 * @returns {string}
 */
export function decodeBuffer(buffer, encoding) {
    const decoder = new TextDecoder(encoding || 'utf-8')
    return decoder.decode(buffer)
}

/**
 * 检测 BOM (Byte Order Mark) 判断编码
 * 返回检测到的编码名称，未检测到则返回 null
 *
 * @param {ArrayBuffer} buffer
 * @returns {string|null} 'utf-8' | 'utf-16le' | 'utf-16be' | null
 */
export function detectBOM(buffer) {
    const bytes = new Uint8Array(buffer)
    if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
        return 'utf-8'
    }
    if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
        return 'utf-16le'
    }
    if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
        return 'utf-16be'
    }
    return null
}

/**
 * 触发浏览器下载文本文件
 * 替代: fs.writeFileSync(path, content) / fs.writeFile(path, content, cb)
 *
 * @param {string} content - 要保存的文本内容
 * @param {string} filename - 建议的文件名
 */
export function downloadTextFile(content, filename) {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    // 延迟释放 URL，确保浏览器完成下载
    setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * 解析文件名，返回 { name, ext }
 * 替代: path.parse(filepath)
 *
 * @param {string} filename - 文件名（可含路径）
 * @returns {{ name: string, ext: string }} ext 包含前导点号，如 ".nc"
 */
export function parseFileName(filename) {
    // 去除路径部分（兼容 Windows 和 Unix 路径分隔符）
    const base = filename.replace(/^.*[\\/]/, '')
    const lastDot = base.lastIndexOf('.')
    if (lastDot <= 0) {
        return { name: base, ext: '' }
    }
    return {
        name: base.substring(0, lastDot),
        ext: base.substring(lastDot) // 包含点号，与 Node path.parse 行为一致
    }
}
